// src/pages/admin/OrderDetail.jsx
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, CheckCircle, Clock, Truck, Package, MapPin,
  FileText, XCircle, CreditCard, User, Building2, AlertTriangle,
} from 'lucide-react'
import { adminOrdersApi } from '@/api/services'
import { useCurrency } from '@/components/common/CurrencySwitcher'
import { Button, StatusBadge, PageSpinner, Card, CardHeader, CardBody, Badge } from '@/components/common/ui'
import toast from 'react-hot-toast'

const STATUS_STEPS = [
  { key: 'confirmed',  label: 'Confirmed',  icon: CheckCircle },
  { key: 'processing', label: 'Processing', icon: Clock },
  { key: 'shipped',    label: 'Shipped',    icon: Truck },
  { key: 'delivered',  label: 'Delivered',  icon: Package },
]

const TRANSITIONS = {
  pending_payment: ['confirmed', 'cancelled'],
  confirmed:       ['processing', 'cancelled'],
  processing:      ['ready_to_ship', 'cancelled'],
  ready_to_ship:   ['shipped'],
  shipped:         ['out_for_delivery', 'delivered'],
  out_for_delivery:['delivered'],
  delivered:       [],
}

const STATUS_ORDER = ['pending_payment', 'confirmed', 'processing', 'ready_to_ship', 'shipped', 'out_for_delivery', 'delivered']

export default function AdminOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { format } = useCurrency()
  const [note, setNote] = useState('')
  const [showCancel, setShowCancel] = useState(false)
  const [cancelReason, setCancelReason] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin-order', id],
    queryFn: () => adminOrdersApi.show(id),
  })

  const updateStatus = useMutation({
    mutationFn: (status) => adminOrdersApi.updateStatus(id, { status, note }),
    onSuccess: () => {
      toast.success('Status updated')
      qc.invalidateQueries({ queryKey: ['admin-order', id] })
      setNote('')
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to update status'),
  })

  const cancel = useMutation({
    mutationFn: () => adminOrdersApi.cancel(id, { reason: cancelReason }),
    onSuccess: () => {
      toast.success('Order cancelled')
      qc.invalidateQueries({ queryKey: ['admin-order', id] })
      setShowCancel(false)
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to cancel'),
  })

  if (isLoading) return <PageSpinner />

  const payload = data?.data?.data || data?.data
  const order = payload?.order || payload
  const gstBreakdown = payload?.gst_breakdown

  if (!order) return <div className="text-center py-16 text-muted">Order not found.</div>

  const isCancelled = order.status === 'cancelled'
  const isDelivered = order.status === 'delivered'
  const canCancel = ['pending_payment', 'confirmed', 'processing'].includes(order.status)
  const nextStatuses = (TRANSITIONS[order.status] || []).filter(s => s !== 'cancelled')

  // Pipeline step mapping
  const stepMap = { confirmed: 0, processing: 1, ready_to_ship: 2, shipped: 2, out_for_delivery: 2, delivered: 3 }
  const activeStep = stepMap[order.status] ?? -1

  // Shipping address
  const hasShippingFields = order.shipping_name || order.shipping_address_line1

  return (
    <div className="max-w-4xl">
      {/* Back */}
      <button onClick={() => navigate('/admin/orders')} className="flex items-center gap-1 text-sm text-muted hover:text-pink mb-4">
        <ArrowLeft size={14} /> Back to Orders
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-xl font-display font-bold flex items-center gap-2">
            {order.order_number} <StatusBadge status={order.status} />
          </h1>
          <p className="text-xs text-muted mt-0.5">
            Placed on {new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          {order.invoice_number && (
            <Button variant="secondary" size="sm"><FileText size={14} /> Invoice</Button>
          )}
        </div>
      </div>

      {/* Visual Status Pipeline */}
      {!isCancelled && (
        <div className="bg-white border border-border rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((step, i) => {
              const isComplete = i < activeStep || isDelivered
              const isCurrent = i === activeStep && !isDelivered
              const Icon = step.icon
              return (
                <div key={step.key} className="flex items-center flex-1 last:flex-initial">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      isComplete ? 'bg-green-500 border-green-500 text-white' :
                      isCurrent ? 'border-pink bg-pink/10 text-pink' :
                      'border-gray-200 bg-gray-50 text-gray-300'
                    }`}>
                      {isComplete ? <CheckCircle size={18} /> : <Icon size={18} />}
                    </div>
                    <span className={`text-[11px] font-bold mt-1.5 ${
                      isComplete ? 'text-green-600' : isCurrent ? 'text-gray-900' : 'text-gray-300'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {i < STATUS_STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-3 mt-[-14px] ${i < activeStep || isDelivered ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </div>
              )
            })}
          </div>

          {isDelivered && order.delivered_at && (
            <p className="text-center text-xs text-green-600 font-bold mt-4">
              Delivered on {new Date(order.delivered_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
      )}

      {/* Cancelled Banner */}
      {isCancelled && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <XCircle size={20} className="text-red-500" />
            <div>
              <p className="font-bold text-sm text-red-800">Order Cancelled</p>
              {order.cancellation_reason && <p className="text-xs text-red-600 mt-0.5">{order.cancellation_reason}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Tracking */}
      {order.tracking_number && (
        <Card className="mb-4 border-indigo-200 bg-indigo-50/30">
          <CardBody>
            <div className="flex items-center gap-3">
              <Truck size={18} className="text-indigo-600" />
              <div>
                <p className="text-sm font-bold text-indigo-800">Shipment Tracking</p>
                <p className="text-sm text-indigo-600">
                  {order.courier_name} — <span className="font-mono font-bold">{order.tracking_number}</span>
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Buyer & Supplier */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <Card>
          <CardHeader><span className="font-bold text-sm flex items-center gap-2"><User size={14} /> Buyer</span></CardHeader>
          <CardBody className="text-sm">
            <p className="font-bold text-gray-900">{order.buyer?.name}</p>
            {order.buyer?.business_name && <p className="text-xs text-muted">{order.buyer.business_name}</p>}
            <p className="text-xs text-muted">{order.buyer?.email}</p>
            {order.buyer?.phone && <p className="text-xs text-muted">{order.buyer.phone}</p>}
          </CardBody>
        </Card>
        <Card>
          <CardHeader><span className="font-bold text-sm flex items-center gap-2"><Building2 size={14} /> Supplier</span></CardHeader>
          <CardBody className="text-sm">
            <p className="font-bold text-gray-900">{order.supplier?.business_name || order.supplier?.name}</p>
            {order.supplier?.business_name && order.supplier?.name && (
              <p className="text-xs text-muted">{order.supplier.name}</p>
            )}
            <p className="text-xs text-muted">{order.supplier?.email}</p>
            {order.supplier?.phone && <p className="text-xs text-muted">{order.supplier.phone}</p>}
          </CardBody>
        </Card>
      </div>

      {/* Items */}
      <Card className="mb-4">
        <CardHeader><span className="font-bold text-sm">Order Items ({order.items?.length || 0})</span></CardHeader>
        <CardBody className="divide-y divide-border p-0">
          {order.items?.map(item => (
            <div key={item.id} className="flex gap-4 p-4">
              <div className="w-14 h-14 rounded-lg bg-surface border border-border flex-shrink-0 overflow-hidden">
                {(item.product?.image || item.product_image || item.product?.primary_image_url) && (
                  <img src={item.product?.image || item.product_image || item.product?.primary_image_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 line-clamp-1">{item.product_name || item.product?.name}</p>
                {item.variant?.color_name && <p className="text-xs text-muted">{item.variant.color_name}</p>}
                <p className="text-xs text-muted">{item.quantity}m × {format(item.unit_price || item.price)}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-gray-900">{format(item.total_price || item.total || (item.quantity * (item.unit_price || item.price)))}</p>
              </div>
            </div>
          ))}
        </CardBody>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        {/* Order Summary */}
        <Card className="md:col-span-2">
          <CardHeader><span className="font-bold text-sm">Order Summary</span></CardHeader>
          <CardBody>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted">Subtotal</span><span>{format(order.subtotal || 0)}</span></div>
              {order.discount_amount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount {order.coupon_code && `(${order.coupon_code})`}</span>
                  <span>−{format(order.discount_amount)}</span>
                </div>
              )}

              {/* GST Breakdown */}
              {gstBreakdown ? (
                <>
                  {gstBreakdown.cgst > 0 && <div className="flex justify-between"><span className="text-muted">CGST ({gstBreakdown.cgst_rate || '9'}%)</span><span>{format(gstBreakdown.cgst)}</span></div>}
                  {gstBreakdown.sgst > 0 && <div className="flex justify-between"><span className="text-muted">SGST ({gstBreakdown.sgst_rate || '9'}%)</span><span>{format(gstBreakdown.sgst)}</span></div>}
                  {gstBreakdown.igst > 0 && <div className="flex justify-between"><span className="text-muted">IGST ({gstBreakdown.igst_rate || '18'}%)</span><span>{format(gstBreakdown.igst)}</span></div>}
                </>
              ) : (order.gst_amount || order.tax_amount) > 0 && (
                <div className="flex justify-between"><span className="text-muted">GST</span><span>{format(order.gst_amount || order.tax_amount)}</span></div>
              )}

              {order.shipping_charge > 0 && <div className="flex justify-between"><span className="text-muted">Shipping</span><span>{format(order.shipping_charge)}</span></div>}
              <div className="flex justify-between font-extrabold pt-2 border-t border-border text-base">
                <span>Total</span><span>{format(order.total_amount)}</span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Payment Info */}
        <Card>
          <CardHeader><span className="font-bold text-sm flex items-center gap-2"><CreditCard size={14} /> Payment</span></CardHeader>
          <CardBody className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted">Method</span>
              <span className="font-bold capitalize">{order.payment_method?.replace(/_/g, ' ') || '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted">Status</span>
              <StatusBadge status={order.payment_status || 'pending'} />
            </div>
            {order.paid_at && (
              <div className="flex justify-between">
                <span className="text-muted">Paid on</span>
                <span className="text-xs">{new Date(order.paid_at).toLocaleDateString('en-IN')}</span>
              </div>
            )}
            {order.payment_reference && (
              <div className="pt-1 border-t border-border">
                <span className="text-[11px] text-muted">Ref: </span>
                <span className="text-[11px] font-mono">{order.payment_reference}</span>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Shipping Address */}
      {(hasShippingFields || order.shipping_address) && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm flex items-center gap-2"><MapPin size={14} /> Shipping Address</span></CardHeader>
          <CardBody className="text-sm text-gray-600">
            {hasShippingFields ? (
              <>
                <p className="font-bold text-gray-900">{order.shipping_name}</p>
                <p>{order.shipping_address_line1}</p>
                {order.shipping_address_line2 && <p>{order.shipping_address_line2}</p>}
                <p>{order.shipping_city}, {order.shipping_state} – {order.shipping_pincode}</p>
                {order.shipping_phone && <p className="mt-1 text-xs text-muted">{order.shipping_phone}</p>}
              </>
            ) : typeof order.shipping_address === 'object' ? (
              <>
                <p className="font-bold text-gray-900">{order.shipping_address.full_name}</p>
                <p>{order.shipping_address.address_line1}</p>
                {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
                <p>{order.shipping_address.city}, {order.shipping_address.state} – {order.shipping_address.pincode}</p>
                {order.shipping_address.phone && <p className="mt-1 text-xs text-muted">{order.shipping_address.phone}</p>}
              </>
            ) : (
              <p>{order.shipping_address}</p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Billing Info */}
      {order.billing_gstin && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm">Billing Details</span></CardHeader>
          <CardBody className="text-sm text-gray-600">
            {order.billing_business_name && <p className="font-bold text-gray-900">{order.billing_business_name}</p>}
            <p className="font-mono text-xs">GSTIN: {order.billing_gstin}</p>
            {order.billing_address_line1 && <p>{order.billing_address_line1}, {order.billing_city}, {order.billing_state} – {order.billing_pincode}</p>}
          </CardBody>
        </Card>
      )}

      {/* Admin Status Update */}
      {nextStatuses.length > 0 && !isCancelled && (
        <Card className="mb-4 border-pink/30">
          <CardHeader><span className="font-bold text-sm flex items-center gap-2"><AlertTriangle size={14} className="text-pink" /> Admin Actions</span></CardHeader>
          <CardBody>
            <input className="input w-full mb-3" placeholder="Internal note (optional)…" value={note} onChange={e => setNote(e.target.value)} />
            <div className="flex gap-2 flex-wrap">
              {nextStatuses.map(s => (
                <Button key={s} size="sm" loading={updateStatus.isPending} onClick={() => updateStatus.mutate(s)}>
                  {s.replace(/_/g, ' ')}
                </Button>
              ))}
              {canCancel && !showCancel && (
                <Button size="sm" variant="danger" onClick={() => setShowCancel(true)}>Cancel Order</Button>
              )}
            </div>

            {showCancel && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm font-bold text-red-800 mb-2">Cancel this order?</p>
                <textarea className="input w-full h-16 mb-2" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancellation…" />
                <div className="flex gap-2">
                  <Button size="sm" variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()} disabled={!cancelReason.trim()}>
                    Confirm Cancel
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowCancel(false)}>Back</Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Order Timeline */}
      {order.status_logs?.length > 0 && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm">Order Timeline</span></CardHeader>
          <CardBody>
            <div className="relative">
              {order.status_logs.map((log, i) => {
                const isLast = i === order.status_logs.length - 1
                return (
                  <div key={i} className="flex gap-3 pb-4 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${isLast ? 'bg-pink ring-4 ring-pink/20' : 'bg-gray-300'}`} />
                      {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
                    </div>
                    <div className="flex-1 -mt-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm capitalize">{(log.to_status || log.status)?.replace(/_/g, ' ')}</span>
                        {log.changed_by_role && <span className="text-[10px] bg-surface px-1.5 py-0.5 rounded capitalize">{log.changed_by_role}</span>}
                        {log.changed_by && <span className="text-[10px] text-muted">by {log.changed_by.name}</span>}
                      </div>
                      {log.note && <p className="text-xs text-gray-600 mt-0.5">{log.note}</p>}
                      <p className="text-[10px] text-muted mt-0.5">{new Date(log.created_at).toLocaleString('en-IN')}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Refunds */}
      {order.refunds?.length > 0 && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm">Refunds</span></CardHeader>
          <CardBody className="divide-y divide-border p-0">
            {order.refunds.map(r => (
              <div key={r.id} className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold">{format(r.amount)}</p>
                  <p className="text-xs text-muted">{r.reason}</p>
                  {r.requested_by && <p className="text-[11px] text-muted">by {r.requested_by.name}</p>}
                </div>
                <StatusBadge status={r.status} />
              </div>
            ))}
          </CardBody>
        </Card>
      )}

      {/* Notes */}
      {(order.buyer_notes || order.supplier_notes || order.admin_notes) && (
        <Card className="mb-4">
          <CardHeader><span className="font-bold text-sm">Notes</span></CardHeader>
          <CardBody className="space-y-2 text-sm">
            {order.buyer_notes && (
              <div>
                <span className="text-xs font-bold text-muted uppercase">Buyer:</span>
                <p className="text-gray-700">{order.buyer_notes}</p>
              </div>
            )}
            {order.supplier_notes && (
              <div>
                <span className="text-xs font-bold text-muted uppercase">Supplier:</span>
                <p className="text-gray-700">{order.supplier_notes}</p>
              </div>
            )}
            {order.admin_notes && (
              <div>
                <span className="text-xs font-bold text-muted uppercase">Admin:</span>
                <p className="text-gray-700">{order.admin_notes}</p>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  )
}
